import { useState } from "react";
import { useTranslation } from "react-i18next";
import Booking from "src/utils/Booking";
import { loadAndApplyAvailableSlots } from "src/utils/availableSlots";
import {
  DATE_AND_TIME_FORMAT,
  createTimeMaps,
  getCalendarMaxDate,
  getCalendarMinDate,
  getPickerValue,
  resetTimeMaps,
  resolveOfferRequestIds,
  shouldDisableCalendarDate,
  snapshotTimeMaps,
} from "src/utils/dateAndTimePicker";

/**
 * Shared calendar, timezone, and available-slot loading for DateAndTime screens.
 * Layout stays in each view; this hook owns the duplicated behavior.
 */
export function useDateAndTimePicker({
  selectedOffer,
  coachDetails,
  selectedTimezone,
  selectedDate,
  setSelectedDate,
  setSelectedTimezone,
  sessionList,
  setSessionList,
  setSessionSelectedTime,
  setOverFlowTimeToRender,
  setUnderFlowTimeToRender,
  setAllUpcommingAppt,
  isUseTimeRecord,
  setIsUseTimeRecord,
  timeRecords,
  setTimeRecords,
  preparingSessionList: preparingSessionListProp,
  setPreparingSesisonList: setPreparingSesisonListProp,
  clearErrors,
  calendarMaxDate,
  offerId,
  userId,
  organizationId,
  excludeAppointmentId,
  userDateFormat,
  userTimeFormat,
  userMinimumBookingNotice,
  nextButtonRef,
  setError,
  mode = "desktop",
  setActiveStep,
  onSlotsLoaded,
  afterDateLoaded,
} = {}) {
  const { t } = useTranslation();
  const timeMaps = createTimeMaps();
  const [internalPreparing, setInternalPreparing] = useState(false);
  const [timezoneDialog, setTimezoneDialog] = useState({ open: false });
  const [isTimezoneChange, setIsTimezoneChange] = useState(false);
  const [timeHover, setTimeHover] = useState(null);

  const preparingSessionList =
    preparingSessionListProp !== undefined
      ? preparingSessionListProp
      : internalPreparing;
  const setPreparingSesisonList =
    setPreparingSesisonListProp ?? setInternalPreparing;

  const requestIds = resolveOfferRequestIds({
    offerId,
    userId,
    organizationId,
    selectedOffer,
  });

  const loadSlotsForSelectedDate = async (date, timezone) => {
    return loadAndApplyAvailableSlots({
      ...requestIds,
      selectedDate: date,
      clientTimezone: timezone,
      excludeAppointmentId,
      setSessionList,
      setOverFlowTimeToRender,
      setUnderFlowTimeToRender,
      setAllUpcommingAppt,
    });
  };

  const shouldDisabledDate = (date) =>
    shouldDisableCalendarDate(date, {
      selectedOffer,
      coachDetails,
      selectedTimezone,
      timeMaps,
      isUseTimeRecord,
      timeRecords,
    });

  const handleSelectedDate = async (date) => {
    if (preparingSessionList) return;
    setPreparingSesisonList(true);
    if (clearErrors) clearErrors();
    if (setSessionSelectedTime) setSessionSelectedTime({});
    setSelectedDate(date);
    setIsTimezoneChange(false);

    if (mode === "mobile") {
      if (setIsUseTimeRecord) setIsUseTimeRecord(true);
      if (setActiveStep) setActiveStep(1);
      if (setTimeRecords) setTimeRecords(snapshotTimeMaps(timeMaps));
    } else if (setIsUseTimeRecord) {
      setIsUseTimeRecord(false);
    }

    try {
      const data = await loadSlotsForSelectedDate(date, selectedTimezone);
      if (onSlotsLoaded) onSlotsLoaded(data, date);
      if (afterDateLoaded) afterDateLoaded(data, date);
    } catch (error) {
      setSessionList([]);
      if (setOverFlowTimeToRender) setOverFlowTimeToRender([]);
      if (setUnderFlowTimeToRender) setUnderFlowTimeToRender([]);
    } finally {
      setPreparingSesisonList(false);
    }
  };

  const handleTimezoneChange = async (timezone) => {
    setSelectedTimezone(timezone);
    setIsTimezoneChange(false);
    if (setIsUseTimeRecord) setIsUseTimeRecord(false);
    if (setOverFlowTimeToRender) setOverFlowTimeToRender([]);
    if (setUnderFlowTimeToRender) setUnderFlowTimeToRender([]);
    resetTimeMaps(timeMaps);

    if (!selectedDate) {
      setSessionList([]);
      return;
    }

    setPreparingSesisonList(true);
    try {
      await loadSlotsForSelectedDate(selectedDate, timezone);
    } catch (error) {
      setSessionList([]);
    } finally {
      setPreparingSesisonList(false);
    }
  };

  const handleSelectedTime = async (time) => {
    if (setIsUseTimeRecord) setIsUseTimeRecord(true);
    if (setTimeRecords) setTimeRecords(snapshotTimeMaps(timeMaps));

    return Booking._dateandtime.handleSelectedTime(
      time,
      userMinimumBookingNotice,
      userDateFormat,
      userTimeFormat,
      setSessionSelectedTime,
      nextButtonRef ?? null,
      setError,
      t
    );
  };

  const openTimezoneDialog = ({ resetSelectedDate = false } = {}) => {
    setTimezoneDialog({ open: true });
    if (resetSelectedDate) {
      setSelectedDate(new Date());
    }
  };

  const closeTimezoneDialog = () => setTimezoneDialog({ open: false });

  const handleMonthChange = () => {
    if (setIsUseTimeRecord) setIsUseTimeRecord(false);
  };

  return {
    dateFormat: DATE_AND_TIME_FORMAT,
    filteredList: sessionList ?? [],
    preparingSessionList,
    timezoneDialog,
    isTimezoneChange,
    timeHover,
    setTimeHover,
    shouldDisabledDate,
    handleSelectedDate,
    handleTimezoneChange,
    handleSelectedTime,
    handleMonthChange,
    openTimezoneDialog,
    closeTimezoneDialog,
    datePickerMinDate: getCalendarMinDate(selectedTimezone),
    datePickerMaxDate: getCalendarMaxDate(calendarMaxDate),
    pickerValue: getPickerValue(selectedDate),
  };
}
